export const COLOR_MODE_KEY = "pos_color_mode";
export const COLOR_MODE_DEFAULT_KEY = "pos_color_mode_default";
export const COLOR_MODE_OVERRIDE_KEY = "pos_color_mode_override";
export const HIGH_CONTRAST_KEY = "pos_high_contrast";
export const COLOR_MODE_EVENT = "pos-color-mode";

const DAY_ONLY_PREFIXES = [
  "/admin/setting",
  "/admin/menus",
  "/admin/mev",
  "/admin/cloud-mev",
  "/admin/printers",
  "/profile",
];

const pathOnly = (pathname = "") => String(pathname).split("?")[0];

export const isKitchenPath = (pathname = "") => /\/kitchen(\/|$)/.test(pathOnly(pathname));

const matchesDayOnlyPrefix = (pathname = "") => {
  const path = pathOnly(pathname);
  if (path === "/profile" || path === "/admin/profile") return true;
  return DAY_ONLY_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

export const isDayOnlyPath = (pathname = "") => {
  const path = pathOnly(pathname);
  if (isKitchenPath(path)) return true;
  return matchesDayOnlyPrefix(path);
};

// Settings, menus, MEV, printers, and the profile stay light. The kitchen board keeps
// its own day/night control, but the app shell still follows the saved color mode.
export const blocksDarkClass = (pathname = "") => matchesDayOnlyPrefix(pathname);

const asMode = (value) => (value === "dark" ? "dark" : value === "light" ? "light" : "");

export const readDefaultColorMode = () => {
  const stored = asMode(localStorage.getItem(COLOR_MODE_DEFAULT_KEY));
  if (stored) return stored;
  const legacy = asMode(localStorage.getItem(COLOR_MODE_KEY));
  if (legacy) {
    localStorage.setItem(COLOR_MODE_DEFAULT_KEY, legacy);
    localStorage.removeItem(COLOR_MODE_KEY);
    return legacy;
  }
  return "light";
};

export const readSessionColorMode = () => asMode(localStorage.getItem(COLOR_MODE_OVERRIDE_KEY));

export const readColorMode = () => readSessionColorMode() || readDefaultColorMode();

export const readHighContrast = () => localStorage.getItem(HIGH_CONTRAST_KEY) === "1";

export const writeHighContrast = (on) => {
  localStorage.setItem(HIGH_CONTRAST_KEY, on ? "1" : "0");
  publish(readColorMode());
  return !!on;
};

const publish = (mode) => {
  window.dispatchEvent(new CustomEvent(COLOR_MODE_EVENT, { detail: mode }));
};

export const writeDefaultColorMode = (mode) => {
  const next = mode === "dark" ? "dark" : "light";
  localStorage.setItem(COLOR_MODE_DEFAULT_KEY, next);
  localStorage.removeItem(COLOR_MODE_OVERRIDE_KEY);
  localStorage.removeItem(COLOR_MODE_KEY);
  publish(next);
  return next;
};

export const writeSessionColorMode = (mode) => {
  const next = mode === "dark" ? "dark" : "light";
  localStorage.setItem(COLOR_MODE_OVERRIDE_KEY, next);
  publish(next);
  return next;
};

export const writeColorMode = writeSessionColorMode;

export const syncStoreDefaultColorMode = (mode) => {
  const next = asMode(mode);
  if (!next) return readColorMode();
  if (localStorage.getItem(COLOR_MODE_DEFAULT_KEY) !== next) {
    localStorage.setItem(COLOR_MODE_DEFAULT_KEY, next);
    localStorage.removeItem(COLOR_MODE_KEY);
    publish(readColorMode());
  }
  return readColorMode();
};

export const preserveDefaultColorMode = () => readDefaultColorMode();

export const restoreDefaultColorMode = (mode) => {
  const next = mode === "dark" ? "dark" : "light";
  localStorage.setItem(COLOR_MODE_DEFAULT_KEY, next);
  localStorage.removeItem(COLOR_MODE_OVERRIDE_KEY);
  localStorage.removeItem(COLOR_MODE_KEY);
  publish(next);
  return next;
};

export const applyColorMode = (mode, pathname = "") => {
  const path = pathOnly(pathname);
  if (path === "/customer") {
    const customerMode = typeof window !== "undefined" ? window.__nuageCustomerColorMode : "";
    document.documentElement.classList.toggle("dark", customerMode === "dark");
    document.documentElement.classList.remove("contrast");
    return;
  }
  const useDark = mode === "dark" && !blocksDarkClass(pathname);
  document.documentElement.classList.toggle("dark", useDark);
  document.documentElement.classList.toggle("contrast", useDark && readHighContrast());
};
